import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Star, Globe, Plus, Loader2, Check, Video, Clock } from "lucide-react";
import axios from "axios";
import { Tx } from "../context/AutoTranslateContext";
import TranslationNotice from "../components/TranslationNotice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanCard = ({ plan, price, sessions, perWeek, highlighted, description }) => (
  <div
    data-testid={`plan-card-${plan}`}
    className={`rounded-3xl p-6 border-2 transition ${
      highlighted ? "border-gold bg-gold/10" : "border-forest/10 bg-cream/60"
    }`}
  >
    <p className="text-[10px] uppercase tracking-widest text-gold mb-2">
      <Tx>{perWeek}</Tx>
    </p>
    <p className="font-heading text-4xl text-forest font-bold mb-1">£{price}</p>
    <p className="text-xs text-forest font-semibold mb-3">
      <Tx>{description}</Tx>
    </p>
    <ul className="space-y-1.5 text-xs text-ink/80">
      <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-forest mt-0.5 flex-shrink-0" /> <Tx>Every session is exactly 30 minutes</Tx></li>
      <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-forest mt-0.5 flex-shrink-0" /> <Tx>Private video call (Jitsi Meet — no download)</Tx></li>
      <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-forest mt-0.5 flex-shrink-0" /> <Tx>Male & female certified tutors</Tx></li>
      <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-forest mt-0.5 flex-shrink-0" /> <Tx>Full refund up to 24h before</Tx></li>
    </ul>
  </div>
);

const Tutors = () => {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({ single: 8, monthly_4: 25, monthly_8: 40 });
  const [langFilter, setLangFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/tutoring/tutors`).then((r) => {
      setTutors(r.data.tutors || []); setLoading(false);
    }).catch(() => setLoading(false));
    axios.get(`${API}/tutoring/prices`).then((r) => {
      const map = {};
      (r.data.plans || []).forEach((p) => { map[p.id] = p.price_gbp; });
      setPrices((cur) => ({ ...cur, ...map }));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="tutors-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link>
        <span>/</span>
        <span className="text-forest"><Tx>Live Tutors</Tx></span>
      </div>

      {/* Hero */}
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3"><Tx>1-to-1 live sessions</Tx></p>
        <h1 className="font-heading text-4xl sm:text-5xl text-forest font-bold mb-4"><Tx>Meet the tutors</Tx></h1>
        <p className="text-mute leading-relaxed max-w-2xl mx-auto">
          <Tx>Qualified male & female tutors for tajweed, ḥifẓ, Ijāza and Arabic grammar. Every session is a strict 30 minutes on a private video call — pick a monthly plan below and stay on the same weekly slot.</Tx>
        </p>
      </div>

      {/* Live AI translation reassurance — shown BEFORE booking */}
      <TranslationNotice className="mb-8" />

      {/* GLOBAL PRICING — visible even before any tutor is approved */}
      <section data-testid="pricing-section" className="mb-14">
        <h2 className="font-heading text-2xl text-forest font-semibold text-center mb-2">
          <Tx>Session plans</Tx>
        </h2>
        <p className="text-xs text-mute text-center mb-6 max-w-lg mx-auto">
          <Tx>Same fixed prices for every tutor — set by the academy management. Cancel any time before the next month.</Tx>
        </p>
        <div className="grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
          <PlanCard plan="single" price={prices.single} sessions={1} perWeek="Try one lesson"
            description="One 30-minute private lesson (paid one time, no monthly commitment)" />
          <PlanCard plan="monthly_4" price={prices.monthly_4} sessions={4} perWeek="1 lesson per week" highlighted
            description="Four 30-minute lessons over one month (one lesson every week at your chosen slot)" />
          <PlanCard plan="monthly_8" price={prices.monthly_8} sessions={8} perWeek="2 lessons per week"
            description="Eight 30-minute lessons over one month (two lessons every week at your chosen slots)" />
        </div>
        <div className="text-center mt-4">
          <p className="text-[11px] text-mute italic inline-flex items-center gap-2">
            <Clock className="w-3 h-3" />
            <Tx>Every session is strictly 30 minutes on a private video call. For monthly plans, the remaining sessions auto-run at the same weekly time.</Tx>
          </p>
        </div>
      </section>

      {/* Tutor list */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-2xl text-forest font-semibold"><Tx>Choose your tutor</Tx></h2>
        <Link
          data-testid="become-tutor-btn"
          to="/academy/tutors/apply"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream border border-forest/20 text-forest hover:border-forest transition text-xs"
        >
          <Plus className="w-3.5 h-3.5" /> <Tx>Are you a tutor?</Tx>
        </Link>
      </div>

      {/* Language filter — students pick a tutor who speaks their language */}
      {!loading && tutors.length > 0 && (
        <div data-testid="lang-filter" className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-[10px] uppercase tracking-widest text-mute mr-1"><Tx>Filter by language</Tx>:</span>
          {["all", ...new Set(tutors.flatMap((t) => t.languages || []))].map((l) => (
            <button key={l} data-testid={`lang-filter-${l}`} onClick={() => setLangFilter(l)}
              className={`text-xs px-3 py-1.5 rounded-full transition ${langFilter === l ? "bg-forest text-sand" : "bg-cream text-mute hover:text-forest"}`}>
              {l === "all" ? <Tx>All</Tx> : l}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="text-center py-12 text-mute">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <p className="text-sm"><Tx>Loading tutors…</Tx></p>
        </div>
      )}

      {!loading && tutors.length === 0 && (
        <div data-testid="tutors-empty" className="text-center py-14 bg-cream/60 rounded-3xl border border-gold/20 max-w-2xl mx-auto">
          <Video className="w-10 h-10 text-gold mx-auto mb-3" />
          <h3 className="font-heading text-xl text-forest font-semibold mb-2">
            <Tx>Tutors are being onboarded right now</Tx>
          </h3>
          <p className="text-sm text-mute mb-4 max-w-md mx-auto">
            <Tx>The management team is currently reviewing tutor applications. The moment the first tutor is approved you'll be able to subscribe here at the fixed monthly price above.</Tx>
          </p>
          <Link
            to="/academy/tutors/apply"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-forest text-sand hover:bg-sage transition text-sm"
          >
            <GraduationCap className="w-4 h-4" /> <Tx>Apply to become a tutor</Tx>
          </Link>
        </div>
      )}

      {!loading && tutors.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tutors.filter((t) => langFilter === "all" || (t.languages || []).includes(langFilter)).map((t) => (
            <Link
              key={t.id} data-testid={`tutor-${t.id}`}
              to={`/academy/tutors/${t.id}`}
              className="clay-surface rounded-3xl p-6 flex flex-col hover:bg-white transition"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-14 h-14 rounded-full bg-forest/10 text-forest flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {t.photo_url
                    ? <img src={t.photo_url} alt="" className="w-full h-full object-cover" />
                    : <GraduationCap className="w-6 h-6" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-lg text-forest font-semibold truncate">{t.full_name}</h3>
                  <p className="text-[10px] uppercase tracking-widest text-mute flex items-center gap-1">
                    <Globe className="w-3 h-3" /> {t.country}
                  </p>
                </div>
                {t.rating_avg != null && (
                  <div className="flex items-center gap-1 text-xs font-semibold text-gold">
                    <Star className="w-3.5 h-3.5 fill-current" />
                    {t.rating_avg.toFixed(1)}
                    <span className="text-mute font-normal">({t.rating_count})</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-mute mb-3 leading-relaxed line-clamp-3">{t.bio}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(t.specialities || []).slice(0, 4).map((s) => (
                  <span key={s} className="text-[10px] uppercase tracking-widest bg-forest/10 text-forest px-2 py-0.5 rounded-full">{s}</span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mb-3">
                {(t.languages || []).slice(0, 4).map((l) => (
                  <span key={l} className="text-[10px] bg-cream text-mute px-2 py-0.5 rounded-full border border-forest/10">{l}</span>
                ))}
                {t.has_recitation && (
                  <span className="text-[10px] inline-flex items-center gap-1 text-gold"><Star className="w-3 h-3 fill-current" /><Tx>Audio sample</Tx></span>
                )}
              </div>
              <div className="mt-auto flex items-center justify-between border-t border-forest/10 pt-3">
                <span className="text-xs text-forest font-semibold">
                  <Tx>From £8 per 30-min lesson</Tx>
                </span>
                <span className="text-xs text-forest font-medium"><Tx>Book →</Tx></span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Tutors;
